/* eslint-disable @typescript-eslint/no-explicit-any */
import Card from "components/UI/Card/Card.js";
import CardHeader from "components/UI/Card/CardHeader.js";
import CardIcon from "components/UI/Card/CardIcon.js";
import { useEffect, useRef, useState } from "react";

import {
  ArrowForwardIosOutlined,
  Cancel,
  CheckCircle,
  ListSharp,
} from "@material-ui/icons";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import Badge from "components/UI/Badge/Badge";
import { useSubscription } from "hooks/useSubscription";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import LoadingBar from "react-top-loading-bar";
import { useReports } from "services/input-core/reports/get-reports";
import { useSOWsQuery } from "services/input-core/sows/get-all-sows";
import { useSuppliersQuery } from "services/input-core/suppliers/get-all-suppliers";
import { useWorkersQuery } from "services/input-core/workers/get-all-workers";

import ReactTable from "components/Tables/ReactTable/ReactTable";
import { sortStatusWithBadge } from "helpers/customSortMethods";
import {
  ActivityItemValidationStatusEnum,
  ProfileStatusEnum,
} from "utils/@types";
import {
  checkMessageErrors,
  compareStringsIgnoreCaseAndRemoveSpecialChars,
  getBadgeFromStatus,
} from "utils/functions";
import { toast } from "react-toastify";
import { ReportsData } from "services/input-core/reports/@types";

const formatWithIcon = (props: any) => (
  <div style={{ display: "flex", textAlign: "start", justifyContent: "start" }}>
    {props.value ? <CheckCircle style={{ color: "#4BB543" }} /> : "_"}
  </div>
);

const formatRejectIcon = (props: any) => (
  <div
    style={{ display: "flex", textAlign: "center", justifyContent: "start" }}
  >
    {props.value ? <Cancel color="error" /> : "_"}
  </div>
);

function StatusFilter({
  column: { filterValue, setFilter },
  statusOptions,
}: {
  column: { filterValue: string; setFilter: (value: string | undefined) => void };
  statusOptions: { value: string; label: string }[];
}) {
  const { t } = useTranslation();

  return (
    <FormControl fullWidth>
      <Select
        style={{ padding: "11px 0px 0px 20px" }}
        variant="standard"
        value={filterValue || ""}
        onChange={(e) => setFilter(e.target.value || undefined)}
      >
        {statusOptions.map((status) => (
          <MenuItem key={status.value} value={status.value}>
            {getBadgeFromStatus(
              status.value === "ACTIVE"
                ? ProfileStatusEnum.ACTIVE
                : ProfileStatusEnum.INACTIVE,
              (text) => t(text, { ns: "status" })
            )}
          </MenuItem>
        ))}
        <MenuItem value="">{t("clear", { ns: "status" })}</MenuItem>
      </Select>
    </FormControl>
  );
}

function SupplierStatusFilter(props: any) {
  const { t } = useTranslation();
  return (
    <StatusFilter
      {...props}
      statusOptions={[
        { value: "ACTIVE", label: t("active") },
        { value: "INACTIVE", label: t("inactive") },
      ]}
    />
  );
}

function SowStatusFilter(props: any) {
  const { t } = useTranslation();
  return (
    <StatusFilter
      {...props}
      statusOptions={[
        { value: "ACTIVE", label: t("active") },
        { value: "INACTIVE", label: t("inactive") },
      ]}
    />
  );
}


const getCSVLabels = (type: string) => {
  switch (type) {
    case "document":
      return "Document";
    case "object":
      return "Object";
    case "uploaded":
      return "Uploaded";
    case "downloaded":
      return "Downloaded";
    case "rejected":
      return "Rejected";
    case "approved":
      return "Approved";
    case "manuallyApproved":
      return "Manually Approved";
    case "concluded":
      return "Concluded";
    case "APPROVED":
      return "Approved";
    case "FINISHED":
      return "Approved";
    case "REJECTED":
      return "Rejected";
    case "MANUAL_VALIDATION":
    case "SUPPLIER_DOWNLOADED":
    case "WAITING_FOR_DOWNLOAD":
    case "WAITING_FOR_APPROVAL":
      return "Not Validated";
    case "PREPARING_FOR_PROCESS":
      return "Preparing";
    case "WAITING_PROCESS":
      return "Processing";
    default:
      return type;
  }
};

export default function BeltrameReportsPage() {
  const { subscription } = useSubscription();
  const ref = useRef(null);
  const tableRef: any = useRef();
  const { t } = useTranslation([
    "supplier-list",
    "list-errors",
    "list-messages",
  ]);

  const [data, setData] = useState<ReportsData>(null);

  const { register, watch, setValue } = useForm();

  useEffect(() => {
    setData(null);
    setValue("triedSearch", false);
  }, [watch("type")]);

  const { mutate: mutateReports, isLoading: isLoadingReports } = useReports();

  const { data: sows, isLoading: isLoadingSow } = useSOWsQuery(subscription);

  const { data: suppliers, isLoading: isLoadingSuppliers } = useSuppliersQuery(
    subscription
  );

  const { data: workers, isLoading: isLoadingWorkers } = useWorkersQuery(
    subscription
  );

  function StatusColumnFilter({ column: { filterValue, setFilter } }: any) {
    const { t } = useTranslation("status");
  
    const statusToFilter = [
      "Not Validated",
      "APPROVED",
      "PREPARING_FOR_PROCESS",
      "REJECTED",
      "WAITING_FOR_APPROVAL",
      "WAITING_PROCESS",
    ];

    return (
      <FormControl fullWidth>
        <Select
          style={{
            padding: "3% 0 0 7%",
            display: "flex",
            flexDirection: "column",
            opacity: filterValue ? 1 : 0.5,
          }}
          variant="standard"
          value={filterValue || ""}
          onChange={(e) => setFilter(e.target.value || undefined)}
        >
          {statusToFilter?.map((status) => (
            <MenuItem
              style={{ padding: "5px 10px", cursor: "pointer" }}
              key={status}
              value={status} 
            >
              {getBadgeFromStatus(status, (text) => t(text, { ns: "status" }))}
            </MenuItem>
          ))}
          <MenuItem
            style={{ padding: "5px 10px", cursor: "pointer" }}
            onClick={() => setFilter(undefined)} 
          >
            {t("clear", { ns: "status" })}
          </MenuItem>
        </Select>
      </FormControl>
    );
  }

  function YesOrNoColumnFilter({ column: { filterValue, setFilter } }: any) {
    return (
      <FormControl fullWidth>
        <Select
          style={{ padding: "11px 0px 0px 20px" }}
          variant="standard"
          value={filterValue || ""}
          onChange={(e: any) => {
            setFilter(e.target.value || undefined); 
          }}
        >
          <MenuItem value="false">{t("no")}</MenuItem>
          <MenuItem value="true">{t("yes")}</MenuItem>
          <MenuItem value="">{t("clear")}</MenuItem>
        </Select>
      </FormControl>
    );
  }

  const columns = [
    {
      Header: t("document"),
      accessor: "document",
    },
    {
      Header: t("object"),
      accessor: "object",
    },
    {
      Header: t("uploaded"),
      accessor: "uploaded",
      Filter: YesOrNoColumnFilter,
      sortType: (a: any) => (a.values.uploaded ? 1 : -1),
      Cell: formatWithIcon,
    },
    {
      Header: t("downloaded"),
      accessor: "downloaded",
      Filter: YesOrNoColumnFilter,
      sortType: (a: any) => (a.values.downloaded ? 1 : -1),
      Cell: formatWithIcon,
    },
    {
      Header: t("reject"),
      accessor: "rejected",
      Filter: YesOrNoColumnFilter,
      sortType: (a: any) => (a.values.rejected ? 1 : -1),
      Cell: formatRejectIcon,
    },
    {
      Header: t("approved"),
      accessor: "approved",
      Filter: YesOrNoColumnFilter,
      sortType: (a: any) => (a.values.approved ? 1 : -1),
      Cell: formatWithIcon,
    },
    {
      Header: t("manually-approved"),
      accessor: "manuallyApproved",
      Filter: YesOrNoColumnFilter,
      sortType: (a: any) => (a.values.manuallyApproved ? 1 : -1),
      Cell: formatWithIcon,
    },
    {
      Header: t("concluded"),
      accessor: "concluded",
      Filter: YesOrNoColumnFilter,
      sortType: (a: any) => (a.values.concluded ? 1 : -1),
      Cell: formatWithIcon,
    },
    {
      Header: " ",
      disableFilters: true,
    },
  ];

  const filterByStatus = (rows: any, columnIds: any, filterValue: any) => {
    if (!filterValue) return rows;
    const column = columnIds[0];

    if (column) {
      if (filterValue === "APPROVED" || filterValue === "REJECTED")
        return rows.filter((row: any) => row.values[column] === filterValue);
      else if (filterValue === "NOT_VALIDATED")
        return rows.filter(
          (row: any) =>
            row.values[column] !== "APPROVED" &&
            row.values[column] !== "REJECTED" &&
            row.values[column] !== "FINISHED"
        );
    }

    return rows;
  };
  

  const filterBySupplierStatus = (
    rows: any,
    columnIds: any,
    filterValue: any
  ) => {
    const column = columnIds[0];

    if (column) {
      if (filterValue === "ACTIVE" || filterValue === "INACTIVE")
        return rows.filter((row: any) => row.values[column] === filterValue);
    }

    return rows;
  };

  const filterBySowStatus = (
    rows: any,
    columnIds: any,
    filterValue: any
  ) => {
    const column = columnIds[0];

    if (column) {
      if (filterValue === "ACTIVE" || filterValue === "INACTIVE")
        return rows.filter((row: any) => row.values[column] === filterValue);
    }

    return rows;
  };

  const workerColumns = [
    {
      Header: t("document"),
      accessor: "document",
    },
    {
      Header: t("code"),
      accessor: "id",
    },
    {
      Header: t("document-status"),
      accessor: "documentStatus",
      Filter: StatusColumnFilter,
      filter: filterByStatus, 
      sortType: sortStatusWithBadge,
      Cell: (info: any) =>
        getBadgeFromStatus(info.value, (text: string) =>
          t(text, { ns: "status" })
        ),
    },
    {
      Header: " ",
      disableFilters: true,
    },
  ];

  const supplierTableColumns = [
    {
      Header: t("code"),
      accessor: "code",
    },
    {
      Header: t("name"),
      accessor: "name",
    },
    {
      Header: t("status"),
      accessor: "supplierStatus",
      Filter: SupplierStatusFilter,
      filter: filterBySupplierStatus,
      Cell: ({ value }: any) =>
        getBadgeFromStatus(
          value === "ACTIVE"
            ? ProfileStatusEnum.ACTIVE
            : ProfileStatusEnum.INACTIVE,
          (text: string) => t(text, { ns: "status" })
        ),
    },
    {
      Header: t("actions"),
      accessor: "actions",
    },
  ];

  const sowTableColumns = [
    {
      Header: t("status"),
      accessor: "sowStatus",
      Filter: SowStatusFilter,
      filter: filterBySowStatus,
      Cell: ({ value }: any) =>
        getBadgeFromStatus(
          value === "ACTIVE"
            ? ProfileStatusEnum.ACTIVE
            : ProfileStatusEnum.INACTIVE,
          (text: string) => t(text, { ns: "status" })
        ),
    },
    {
      Header: t("id"),
      accessor: "sowID",
    },
    {
      Header: t("name"),
      accessor: "sowName",
    },
    {
      Header: t("supplier"),
      accessor: "sowSupplier",
    },
    {
      Header: t("actions"),
      accessor: "actions",
    },
  ];

  const workerTableColumns = [
    {
      Header: t("code"),
      accessor: "code",
    },
    {
      Header: t("name"),
      accessor: "name",
    },
    {
      Header: t("email"),
      accessor: "email",
    },
    {
      Header: t("dni"),
      accessor: "numberDNI",
    },
    {
      Header: t("vendor-code"),
      accessor: "vendorCode",
    },
    {
      Header: t("vendor-name"),
      accessor: "vendorName",
    },
    {
      Header: t("actions"),
      accessor: "actions",
    },
  ];

  const getColumnByType = (type: string) => {
    switch (type) {
      case "Supplier":
        return supplierTableColumns;
      case "Sow":
        return sowTableColumns;
      case "Worker":
        return workerTableColumns;
      default:
        return [];
    }
  };

  const getDataByType = (type: string) => {
    switch (type) {
      case "Supplier":
        return suppliers?.map((supplier, key) => ({
          id: key,
          code: supplier.supplierCode,
          name: supplier.supplierName,
          supplierStatus: compareStringsIgnoreCaseAndRemoveSpecialChars(
            supplier.status,
            ProfileStatusEnum.ACTIVE
          )
            ? "ACTIVE" 
            : "INACTIVE", 
          actions: (
            <div className="actions-right">
              <Button
                size="small"
                variant="contained"
                color="info"
                disabled={isLoadingReports}
                style={{
                  padding: 5,
                  borderRadius: 50,
                }}
                onClick={() => {
                  setValue("name", supplier.supplierName);
                  setValue("code", supplier.supplierCode);
                  handleGetReports(supplier.supplierCode);
                }}
              >
                <Tooltip title={t("get-report")}>
                  {isLoadingReports &&
                  watch("code") === supplier.supplierCode ? (
                    <CircularProgress size={26} />
                  ) : (
                    <ArrowForwardIosOutlined style={{ fontSize: 20 }} />
                  )}
                </Tooltip>
              </Button>
            </div>
          ),
        }));

      case "Sow":
        return sows?.map((sowItem, key) => ({
          id: key,
          sowStatus:  compareStringsIgnoreCaseAndRemoveSpecialChars(
            sowItem.status,
            ProfileStatusEnum.ACTIVE
          )
            ? "ACTIVE" 
            : "INACTIVE", 
          sowID: sowItem.sowID,
          sowName: sowItem.sowName,
          sowSupplier: sowItem.supplierName,
          actions: (
            <div className="actions-right">
              <Button
                variant="contained"
                color="info"
                style={{
                  padding: 5,
                  borderRadius: 50,
                }}
                disabled={isLoadingReports}
                onClick={() => {
                  setValue("name", sowItem.sowName);
                  setValue("code", sowItem.sowID);
                  handleGetReports(sowItem.sowID);
                }}
              >
                <Tooltip title={t("get-report")}>
                  {isLoadingReports && watch("code") === sowItem.sowID ? (
                    <CircularProgress size={26} />
                  ) : (
                    <ArrowForwardIosOutlined style={{ fontSize: 20 }} />
                  )}
                </Tooltip>
              </Button>
            </div>
          ),
        }));
      case "Worker":
        return workers?.map((prop, key) => ({
          id: key,
          code: prop.workerID,
          name: `${prop.firstName} ${prop.lastName}`,
          email: prop.workerEmail,
          numberDNI:
            prop?.internalFields?.length > 0
              ? prop?.internalFields[0]?.documentNumber
              : "",
          vendorCode: prop.vendorNumber,
          vendorName: prop.vendorName,
          actions: (
            <div className="actions-right">
              <Button
                variant="contained"
                color="info"
                style={{
                  padding: 5,
                  borderRadius: 50,
                }}
                disabled={isLoadingReports}
                onClick={() => {
                  setValue("name", `${prop.firstName} ${prop.lastName}`);
                  setValue("code", prop.workerID);
                  handleGetReports(prop.workerID);
                }}
              >
                <Tooltip title={t("get-report")}>
                  <ArrowForwardIosOutlined style={{ fontSize: 20 }} />
                </Tooltip>
              </Button>
            </div>
          ),
        }));
      default:
        return [];
    }
  };

  const JSONToCSVConvertor = (rows: any) => {
    const ReportTitle: string = watch("name") + watch("code");
    const arrData = rows.map((row: any) => row.original);

    let CSV = "";

    let row = "";
    for (const index in arrData[0]) {
      row += getCSVLabels(index) + ",";
    }
    row = row.slice(0, -1);
    CSV += row + "\r\n";

    for (let i = 0; i < arrData.length; i++) {
      let row = "";
      for (const index in arrData[i]) {
        row +=
          '"' +
          (arrData[i][index]
            ? arrData[i][index] === true
              ? "X"
              : getCSVLabels(arrData[i][index])
            : "") +
          '",';
      }
      row.slice(0, row.length - 1);
      CSV += row + "\r\n";
    }

    if (CSV === "") {
      toast.error(t("danger-report-generation", { ns: "list-messages" }));
      return;
    }

    let fileName = "report";
    fileName += ReportTitle.replace(/ /g, "_");

    const uri =
      "data:text/csv;charset=utf-8," +
      CSV.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    const link = document.createElement("a");
    link.href = uri;

    link.download = fileName + ".csv";

    document.body.appendChild(link);
    link.click();
    if (link.parentNode === document.body) document.body.removeChild(link);
  };

  const handleGetReports = async (code: string) => {
    setValue("triedSearch", true);

    if (watch("type"))
      mutateReports(
        {
          filter: code,
          type: watch("type")?.toLowerCase(),
        },
        {
          onSuccess: (data) => {
            if (
              data?.sowActivities.length === 0 &&
              data?.workerActivities.length === 0 &&
              data?.supplierActivities.length === 0
            )
              toast.info(t("info-data-found", { ns: "list-messages" }));
            else
              toast.success(`
              ${t("success-getting-report", {
                ns: "list-messages",
              })}${code}`);

            setData(data);
          },
          onError: (error: any) => {
            toast.error(
              t(
                checkMessageErrors(
                  error?.response?.data?.error,
                  "ERR_GETTING_REPORTS_DEFAULT"
                ),
                { ns: "list-errors" }
              )
            );
          },
        }
      );
  };

  const renderActivities = (type: string) => {
    switch (type) {
      case "Sow":
        return data.sowActivities;
      case "Worker":
        return data.workerActivities;
      case "Supplier":
        return data.supplierActivities;
      default:
        return [];
    }
  };

  const getOverallStatus = () => {
    return data.workerActivities.some(
      (element) =>
        !compareStringsIgnoreCaseAndRemoveSpecialChars(
          element.approved === true
            ? ActivityItemValidationStatusEnum.APPROVED
            : "",
          ActivityItemValidationStatusEnum.APPROVED
        )
    )
      ? getBadgeFromStatus(
          ActivityItemValidationStatusEnum.REJECTED,
          (text: string) => t(text, { ns: "status" })
        )
      : getBadgeFromStatus(
          ActivityItemValidationStatusEnum.APPROVED,
          (text: string) => t(text, { ns: "status" })
        );
  };

  return (
    <>
      <div>
        <LoadingBar color="#ff9800" ref={ref} />

        <Card>
          <CardHeader color="rose" icon>
            <CardIcon color="rose">
              <ListSharp />
            </CardIcon>
          </CardHeader>

          <Grid
            container
            style={{
              padding: 20,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            spacing={3}
          >
            <Grid item xs>
              <FormControl
                style={{ minWidth: "200px", marginRight: 50 }}
                disabled={
                  isLoadingSow || isLoadingSuppliers || isLoadingWorkers
                }
              >
                <InputLabel
                  id="sa"
                  disabled={
                    isLoadingSow || isLoadingSuppliers || isLoadingWorkers
                  }
                >
                  {t("report-type")}
                </InputLabel>
                <Select
                  variant="standard"
                  className="selectField"
                  disabled={
                    isLoadingSow || isLoadingSuppliers || isLoadingWorkers
                  }
                  {...register("type")}
                >
                  <MenuItem value="Supplier">{t("supplier")}</MenuItem>
                  <MenuItem value="Sow">{t("sow")}</MenuItem>
                  <MenuItem value="Worker">{t("worker")}</MenuItem>
                </Select>
              </FormControl>
              {watch("triedSearch") && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setValue("triedSearch", false);
                  }}
                >
                  {t("new-search")}
                </Button>
              )}

              {watch("type") && !watch("triedSearch") && (
                <div style={{ padding: 20 }}>
                  <ReactTable
                    columns={getColumnByType(watch("type"))}
                    data={getDataByType(watch("type"))}
                    columnsNotTrimFilters={[
                      "name",
                      "vendorName",
                      "sowName",
                      "sowSupplier",
                    ]}
                  />
                </div>
              )}
            </Grid>
          </Grid>
        </Card>

        {isLoadingReports ? (
          <Card>
            <Box style={{ padding: "20px", width: "100%", display: "flex" }}>
              <Box
                style={{
                  display: "flex",
                  alignItems: `center`,
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={40} />
                <Typography
                  style={{
                    marginLeft: 10,
                  }}
                >
                  {t("searching-for")}: {watch(`code`)}
                </Typography>
              </Box>
            </Box>
          </Card>
        ) : data?.sowActivities.length > 0 ||
          data?.workerActivities.length > 0 ||
          data?.supplierActivities.length > 0 ? (
          <Card>
            <Box style={{ padding: "20px", width: "100%", display: "flex" }}>
              <Box style={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="subtitle1">
                  {t("name")}: {watch("name")}
                </Typography>
                <Typography variant="subtitle1">
                  {t("code")}: {watch("code")}
                </Typography>
              </Box>

              {watch("type") === "worker" && (
                <Box
                  style={{
                    marginLeft: "auto",
                    marginRight: 20,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    style={{
                      textAlign: "center",
                    }}
                  >
                    Overall Status
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    style={{
                      textAlign: "center",
                    }}
                  >
                    {getOverallStatus()}
                  </Typography>
                </Box>
              )}
            </Box>

            <Grid style={{ padding: "20px" }}>
              <ReactTable
                ref={tableRef}
                exportToCSVFunction={JSONToCSVConvertor}
                data={renderActivities(watch("type"))}
                columns={watch("type") === "Worker" ? workerColumns : columns}
                columnsNotTrimFilters={["document", "object"]}
              />
            </Grid>
          </Card>
        ) : (
          watch("triedSearch") &&
          !isLoadingReports && (
            <Card>
              <Box style={{ padding: "20px", width: "100%", display: "flex" }}>
                <Box style={{ display: "flex", flexDirection: "column" }}>
                  <Typography>{t("no-data-found")}</Typography>
                </Box>
              </Box>
            </Card>
          )
        )}
      </div>
    </>
  );
}
